import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, rankByTier } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { AtomIcon, TrophyIcon } from "@phosphor-icons/react";
import BotName from "@/components/BotName";

const RANK_TONE = [
  { color: "#D6FF38", label: "1st" },
  { color: "#94A3B8", label: "2nd" },
  { color: "#B87333", label: "3rd" },
];

export default function StrategyLeaders() {
  const [families, setFamilies] = useState([]);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [params] = useSearchParams();

  useEffect(() => {
    Promise.all([api.get("/strategies"), api.get("/bots")])
      .then(([s, b]) => {
        setFamilies(s.data.strategies || []);
        setBots(b.data.bots || []);
      })
      .finally(() => setLoading(false));
  }, []);

  // Auto-expand from ?family= query param
  useEffect(() => {
    const q = params.get("family");
    if (q) setExpanded(q);
  }, [params]);

  const byFamily = useMemo(() => {
    const groups = {};
    for (const b of bots) {
      const fam = (b.strategy_family || "").trim() || "UNKNOWN";
      (groups[fam] ||= []).push(b);
    }
    for (const k in groups) groups[k].sort(rankByTier);
    return groups;
  }, [bots]);

  if (loading) {
    return <div data-testid="strategy-leaders-loading" className="p-6 mono text-sm text-[#94A3B8]">Loading strategy leaders…</div>;
  }

  return (
    <div data-testid="strategy-leaders-page" className="p-4 md:p-6 space-y-5">
      <div>
        <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">Per-Strategy Podium</div>
        <h1 className="text-xl md:text-2xl tracking-tight font-medium mt-1 text-[#F1F5F9]">Strategy Leaders</h1>
        <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl leading-relaxed">
          Top-3 bots for each strategy family in your fleet. Click any family to expand
          the full list of bots that share that DNA.
        </p>
      </div>

      <div className="text-[11px] mono text-[#94A3B8]">
        {families.length} families · {bots.length} bots total
      </div>

      <div className="grid grid-cols-1 gap-4">
        {families.map((f) => {
          const topThree = (byFamily[f.code] || []).slice(0, 3);
          const isOpen = expanded === f.code;
          const allForFamily = byFamily[f.code] || [];
          return (
            <div
              key={f.code}
              data-testid={`strategy-card-${f.code}`}
              className="border border-[#1F2937] bg-[#0F172A] rounded"
            >
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[#151F33] transition-colors"
                onClick={() => setExpanded(isOpen ? null : f.code)}
              >
                <div className="flex items-center gap-3">
                  <AtomIcon size={18} className="text-[#D6FF38]" />
                  <div>
                    <div className="text-sm mono text-[#F1F5F9]">
                      <span className="text-[#D6FF38]">{f.code}</span>
                      <span className="text-[#94A3B8] ml-2">· {f.label}</span>
                    </div>
                    <div className="text-[10px] mono text-[#64748B] mt-0.5 uppercase tracking-widest">
                      {f.total_bots} bots · {f.active_bots} active · avg growth {f.avg_growth_pct}%
                    </div>
                  </div>
                </div>
                <div className="text-[10px] mono text-[#94A3B8]">{isOpen ? "collapse ▲" : "expand ▼"}</div>
              </div>

              {/* Podium (always visible) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 px-4 pb-3">
                {topThree.map((b, i) => (
                  <Link
                    key={b.id}
                    to={`/bot/${b.id}`}
                    data-testid={`strategy-podium-${f.code}-${i}`}
                    className="border border-[#1F2937] bg-[#0B0C10] rounded px-3 py-2 hover:border-[#D6FF38]/30 transition-colors block"
                  >
                    <div className="flex items-center justify-between">
                      <TrophyIcon size={14} style={{ color: RANK_TONE[i]?.color }} />
                      <span className="text-[9px] mono uppercase tracking-widest text-[#64748B]">{RANK_TONE[i]?.label}</span>
                    </div>
                    <div className="mt-1 text-sm mono truncate text-[#F1F5F9]">
                      <BotName name={b.name} />
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <StatusBadge status={b.status} />
                      <span className="text-[10px] mono text-[#94A3B8]">{b.symbol || "—"}</span>
                    </div>
                    <div className="mt-1 text-[10px] mono text-[#94A3B8]">
                      growth <span className="text-[#F1F5F9]">{(b.growth_pct ?? 0).toFixed(1)}%</span>
                      {" · "}dd <span className="text-[#F1F5F9]">{(b.max_drawdown ?? 0).toFixed(1)}%</span>
                    </div>
                  </Link>
                ))}
                {topThree.length === 0 && (
                  <div className="text-[11px] mono text-[#94A3B8] italic">no bots yet — waiting for the AI to seed this family</div>
                )}
              </div>

              {/* Expanded full list */}
              {isOpen && allForFamily.length > 3 && (
                <div data-testid={`strategy-full-list-${f.code}`} className="border-t border-[#1F2937] px-4 py-3">
                  <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-2">
                    All {allForFamily.length} bots in {f.code}
                  </div>
                  <div className="space-y-1 max-h-96 overflow-y-auto">
                    {allForFamily.map((b) => (
                      <Link
                        key={b.id}
                        to={`/bot/${b.id}`}
                        className="flex items-center justify-between text-[11px] mono py-1 hover:text-[#D6FF38]"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <StatusBadge status={b.status} />
                          <span className="truncate text-[#F1F5F9]"><BotName name={b.name} /></span>
                          <span className="text-[#64748B]">{b.symbol || "—"}</span>
                        </div>
                        <div className="text-[#94A3B8] ml-2">
                          {(b.growth_pct ?? 0).toFixed(1)}%
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
